import { useMemo, useState } from "react";

import { Button, Dialog, Field, Input, Notice, Select, Textarea, plainError } from "../../../../components/ui";
import { BeforeAfter } from "./CorrectionDetail";
import { CLASSIFICATION, adjustmentsApi, newRequestKey, toCents } from "./adjustmentView";

// Report 54B (D2): HR raises a correction from a paid payslip. HR states the corrected FACT (a day, minutes, a
// deduction line); the server works out the money by replaying the payroll formula on the payslip's stored figures and
// shows it before anything is saved. Where the server cannot work it out, it says why, and HR may enter a reviewed
// amount with its basis instead. HR never types a net pay.

const WAGE_FACTS = [
  { value: "absent_less", label: "A day counted absent was worked", unit: "days" },
  { value: "absent_more", label: "A day paid was actually absent", unit: "days" },
  { value: "leave_paid", label: "Leave treated as unpaid should be paid", unit: "days" },
  { value: "leave_unpaid", label: "Leave treated as paid should be unpaid", unit: "days" },
  { value: "overtime_more", label: "Approved overtime was left out", unit: "minutes" },
  { value: "overtime_less", label: "Overtime was paid but not worked", unit: "minutes" },
];
const UNDER = new Set(["absent_less", "leave_paid", "overtime_more"]);

function correctionOf(fact, quantity, dates, dayType) {
  const n = Number(quantity);
  const base = { dates: dates.split(",").map((value) => value.trim()).filter(Boolean) };
  switch (fact) {
    case "absent_less": return { ...base, absentDays: -n };
    case "absent_more": return { ...base, absentDays: n };
    case "leave_paid": return { ...base, unpaidLeaveToPaidDays: n };
    case "leave_unpaid": return { ...base, unpaidLeaveToPaidDays: -n };
    case "overtime_more": return { ...base, overtimeMinutes: n, dayType };
    case "overtime_less": return { ...base, overtimeMinutes: -n, dayType };
    default: return base;
  }
}

export default function RaiseCorrectionDialog({ payslip, onClose, onCreated }) {
  const [kind, setKind] = useState("WAGE_UNDERPAYMENT");
  const [fact, setFact] = useState("absent_less");
  const [quantity, setQuantity] = useState("1");
  const [dates, setDates] = useState("");
  const [dayType, setDayType] = useState("ordinary");
  const [amount, setAmount] = useState("");
  const [cashDirection, setCashDirection] = useState("RECOVER_FROM_EMPLOYEE");
  const [lineId, setLineId] = useState("");
  const [agency, setAgency] = useState("");
  const [reviewedBasis, setReviewedBasis] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [method, setMethod] = useState("CASH_RETURN");
  const [recoveryBasis, setRecoveryBasis] = useState("");
  const [authorisation, setAuthorisation] = useState("");
  const [installment, setInstallment] = useState("");
  const [preview, setPreview] = useState(null);
  const [useReviewed, setUseReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key] = useState(newRequestKey);

  const wage = kind === "WAGE_UNDERPAYMENT" || kind === "WAGE_OVERPAYMENT";
  const facts = WAGE_FACTS.filter((item) => UNDER.has(item.value) === (kind === "WAGE_UNDERPAYMENT"));
  const deductions = Array.isArray(payslip.deductions) ? payslip.deductions : [];
  const statutory = [...(payslip.statutory?.lines || []), ...(payslip.statutory?.withholding ? [{ ...payslip.statutory.withholding, agency: "BIR_WHT" }] : [])];
  const cents = toCents(amount);

  const payload = useMemo(() => {
    const body = { payslip_id: payslip.id, classification: kind, reason: reason.trim(), evidence_reference: evidence.trim() || undefined };
    if (wage) {
      if (useReviewed) body.reviewed = { gross_difference_cents: cents, basis: reviewedBasis.trim() };
      body.correction = correctionOf(fact, quantity, dates, dayType);
    } else if (kind === "CASH_HANDOVER_DISCREPANCY") {
      body.amount_cents = cents;
      body.direction = cashDirection;
    } else if (kind === "DEDUCTION_ERROR") {
      body.payslip_deduction_id = Number(lineId) || undefined;
      body.amount_cents = cents;
    } else {
      body.agency = agency;
      body.corrected_employee_cents = cents;
    }
    return body;
  }, [payslip.id, kind, reason, evidence, wage, useReviewed, cents, reviewedBasis, fact, quantity, dates, dayType, cashDirection, lineId, agency]);

  const recovering = preview?.direction === "RECOVER_FROM_EMPLOYEE";
  const reset = () => { setPreview(null); setUseReviewed(false); setError(""); };
  const change = (setter) => (event) => { setter(event.target.value); reset(); };

  const work = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await adjustmentsApi.preview(payload);
      setPreview(result);
      if (result.needsReviewedAmount) setUseReviewed(true);
    } catch (err) {
      setError(plainError(err, "The correction couldn't be worked out. Check what you entered."));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { ...payload };
      if (recovering) {
        body.recovery_method = method;
        if (recoveryBasis.trim()) body.recovery_basis = recoveryBasis.trim();
        if (method === "FUTURE_PAY") {
          body.employee_authorization_ref = authorisation.trim();
          body.installment_cents = toCents(installment);
        }
      }
      const created = await adjustmentsApi.create(body, key);
      onCreated(created);
    } catch (err) {
      setError(plainError(err, "The correction couldn't be saved. Nothing was changed."));
    } finally {
      setBusy(false);
    }
  };

  const ready = preview && !preview.needsReviewedAmount && reason.trim().length >= 10
    && (!recovering || method === "CASH_RETURN" || (recoveryBasis.trim().length >= 20 && authorisation.trim().length >= 5 && toCents(installment) > 0));

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }} size="lg"
      title={`Raise a correction — ${payslip.member_name || "employee"}`}
      description={`${payslip.payslip_number || "Payslip"} · ${payslip.period_name || ""}. The payslip itself is not changed.`}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          {ready
            ? <Button variant="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save as draft"}</Button>
            : <Button variant="primary" onClick={work} disabled={busy}>{busy ? "Working it out…" : "Work it out"}</Button>}
        </>
      )}>
      <div className="space-y-4">
        <Field label="What is wrong" hint={CLASSIFICATION[kind].hint}>
          <Select value={kind} onChange={(event) => { setKind(event.target.value); setFact(event.target.value === "WAGE_OVERPAYMENT" ? "absent_more" : "absent_less"); reset(); }}>
            {Object.entries(CLASSIFICATION).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
          </Select>
        </Field>

        {wage ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="The corrected fact">
              <Select value={fact} onChange={change(setFact)}>{facts.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Select>
            </Field>
            <Field label={WAGE_FACTS.find((item) => item.value === fact)?.unit === "minutes" ? "Minutes" : "Days"} hint={WAGE_FACTS.find((item) => item.value === fact)?.unit === "minutes" ? "Whole minutes." : "Whole or half days."} required>
              <Input inputMode="decimal" value={quantity} onChange={change(setQuantity)} />
            </Field>
            <Field label="Date or dates" hint="As YYYY-MM-DD, separated by commas. Shown with the correction.">
              <Input value={dates} onChange={change(setDates)} placeholder="2026-08-19" />
            </Field>
            {fact.startsWith("overtime") ? (
              <Field label="The day was" hint="Only ordinary working days are worked out automatically.">
                <Select value={dayType} onChange={change(setDayType)}>
                  <option value="ordinary">An ordinary working day</option>
                  <option value="rest_day">A rest day</option>
                  <option value="holiday">A holiday</option>
                  <option value="night">Night work</option>
                </Select>
              </Field>
            ) : null}
          </div>
        ) : null}

        {kind === "CASH_HANDOVER_DISCREPANCY" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="The employee received">
              <Select value={cashDirection} onChange={change(setCashDirection)}>
                <option value="RECOVER_FROM_EMPLOYEE">More cash than the payslip's net pay</option>
                <option value="PAY_EMPLOYEE">Less cash than the payslip's net pay</option>
              </Select>
            </Field>
            <Field label="By how much (₱)" error={amount && cents == null ? "Enter an amount in pesos." : undefined} required>
              <Input inputMode="decimal" value={amount} onChange={change(setAmount)} />
            </Field>
          </div>
        ) : null}

        {kind === "DEDUCTION_ERROR" ? (
          deductions.length ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="The deduction" required>
                <Select value={lineId} onChange={change(setLineId)}>
                  <option value="">Choose a deduction on this payslip</option>
                  {deductions.map((row) => <option key={row.id} value={row.id}>{row.deduction_name} — ₱{(Number(row.amount_cents) / 100).toFixed(2)}</option>)}
                </Select>
              </Field>
              <Field label="Amount taken in error (₱)" error={amount && cents == null ? "Enter an amount in pesos." : undefined} required>
                <Input inputMode="decimal" value={amount} onChange={change(setAmount)} />
              </Field>
            </div>
          ) : <Notice>This payslip has no loan or other deduction to correct.</Notice>
        ) : null}

        {kind === "STATUTORY_OR_WITHHOLDING_ERROR" ? (
          statutory.length ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="The contribution or tax" required>
                <Select value={agency} onChange={change(setAgency)}>
                  <option value="">Choose one on this payslip</option>
                  {statutory.map((row) => <option key={row.agency} value={row.agency}>{row.agency === "BIR_WHT" ? "Withholding tax" : row.agency} — ₱{(Number(row.employeeCents || 0) / 100).toFixed(2)}</option>)}
                </Select>
              </Field>
              <Field label="What the employee amount should be (₱)" hint="A proposal. Finance decides the treatment." error={amount && cents == null ? "Enter an amount in pesos." : undefined} required>
                <Input inputMode="decimal" value={amount} onChange={change(setAmount)} />
              </Field>
            </div>
          ) : <Notice>This payslip has no contribution or tax line.</Notice>
        ) : null}

        <Field label="Why this correction is needed" hint="At least 10 characters. The employee and Finance read this." required>
          <Textarea rows={2} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        <Field label="Evidence" hint={kind === "CASH_HANDOVER_DISCREPANCY" ? "Required: what shows the cash really handed over (a signed count sheet, a note)." : "Where the supporting record is kept (optional)."} required={kind === "CASH_HANDOVER_DISCREPANCY"}>
          <Input maxLength={500} value={evidence} onChange={(event) => setEvidence(event.target.value)} />
        </Field>

        {preview?.needsReviewedAmount || (useReviewed && wage) ? (
          <Notice tone="warning" title="This cannot be worked out automatically">
            <p>{preview?.reason}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Reviewed change in gross pay (₱)" error={amount && cents == null ? "Enter an amount in pesos." : undefined} required>
                <Input inputMode="decimal" value={amount} onChange={(event) => { setAmount(event.target.value); setPreview((current) => (current ? { ...current, needsReviewedAmount: true } : current)); }} />
              </Field>
              <Field label="Basis of the amount" hint="At least 20 characters: the rate, quantity and record you used." required>
                <Textarea rows={2} maxLength={600} value={reviewedBasis} onChange={(event) => { setReviewedBasis(event.target.value); setPreview((current) => (current ? { ...current, needsReviewedAmount: true } : current)); }} />
              </Field>
            </div>
            <p className="mt-2 text-meta">Then choose Work it out again. The correction is marked as entered by a person, not worked out automatically.</p>
          </Notice>
        ) : null}

        {preview && !preview.needsReviewedAmount ? (
          <section aria-label="The correction worked out" className="rounded-panel border border-default p-4">
            <h3 className="text-panel-title text-foreground">
              {preview.calculationMode === "AUTOMATIC" ? "Worked out from the payslip's stored figures" : "Entered amount"}
            </h3>
            {preview.summary ? <p className="text-body-sm text-secondary">{preview.summary}</p> : null}
            <BeforeAfter lines={preview.lines} />
            <p className="mt-2 text-body-sm text-secondary">
              {preview.statutoryState === "REVIEW_REQUIRED"
                ? "The effect on contributions and tax is not known yet. Finance records it before this can be approved, paid or recovered."
                : preview.statutoryBasis}
            </p>
            {preview.restoresLoanBalance ? <p className="mt-1 text-meta text-secondary">When the refund is paid, the amount goes back on the loan balance.</p> : null}
          </section>
        ) : null}

        {preview && !preview.needsReviewedAmount && recovering ? (
          <section aria-label="How it is recovered" className="space-y-3">
            <Field label="How the amount is to be recovered" hint="A claim on the employee is not a debt until it is shown and approved. A deduction from future pay needs a lawful basis and the employee's written authorisation.">
              <Select value={method} onChange={(event) => setMethod(event.target.value)}>
                <option value="CASH_RETURN">The employee returns cash</option>
                <option value="FUTURE_PAY">From future pay, in installments</option>
              </Select>
            </Field>
            {method === "FUTURE_PAY" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Lawful or policy basis" hint="At least 20 characters." required>
                  <Textarea rows={2} maxLength={1000} value={recoveryBasis} onChange={(event) => setRecoveryBasis(event.target.value)} />
                </Field>
                <div className="space-y-3">
                  <Field label="Employee's written authorisation" hint="Where the signed authorisation is kept." required>
                    <Input maxLength={500} value={authorisation} onChange={(event) => setAuthorisation(event.target.value)} />
                  </Field>
                  <Field label="Installment per payroll (₱)" required>
                    <Input inputMode="decimal" value={installment} onChange={(event) => setInstallment(event.target.value)} />
                  </Field>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {error ? <Notice tone="danger" role="alert">{error}</Notice> : null}
      </div>
    </Dialog>
  );
}
